import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, Shield, Zap, CheckCircle, Phone, Mail, MapPin,
  Users, Briefcase, DollarSign, Clock, Award, Building, CreditCard,
  FileText, BookOpen, Rocket, Handshake, BarChart, Factory, Store,
  GraduationCap, Calculator, ArrowRight, Calendar, BadgeCheck, Percent
} from 'lucide-react';

const MSMELoan = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    businessType: '',
    loanAmount: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('MSME Loan inquiry:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-green-800 to-green-700 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in-up">
              Grow Your Business with MSME Loan in Varanasi
            </h1>
            <p className="text-xl md:text-2xl mb-4 animate-fade-in-up animation-delay-200">
              Fast Approval | Low Interest | Minimum Documentation
            </p>
            <p className="text-lg mb-8 animate-fade-in-up animation-delay-300">
              Get an <strong className="text-yellow-300">easy and quick MSME Loan</strong> with CashPro Financial Services — funding for business expansion, working capital, and growth is now simple!
            </p>
            <button className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg animate-fade-in-up animation-delay-400">
              Apply Now & Get Quick Approval
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-12">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="white"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* About MSME Loan */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">What is an MSME Loan?</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <p className="text-gray-700 text-lg leading-relaxed">
              An MSME Loan is a <strong className="text-green-600">business funding solution</strong> designed to support small and medium enterprises.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-green-600" />
                <span>Funds for business expansion</span>
              </div>
              <div className="flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-green-600" />
                <span>Working capital for daily operations</span>
              </div>
              <div className="flex items-center gap-3">
                <Building className="w-6 h-6 text-green-600" />
                <span>Loan for machinery or equipment purchase</span>
              </div>
            </div>
            <p className="text-gray-700 mt-4 text-center text-lg">
              → MSME Loan takes your business to the next level 🚀
            </p>
          </div>
        </div>

        {/* Why Choose CashPro */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Why Choose CashPro for MSME Loan?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: "Fast Loan Approval Process", desc: "Quick decisions, no delays" },
              { icon: Percent, title: "Lowest Interest Rate Options", desc: "Starting from 8% p.a." },
              { icon: Shield, title: "Collateral-Free Loan", desc: "Unsecured business funding" },
              { icon: DollarSign, title: "Loan Amount from ₹50,000 to ₹1 Crore+", desc: "Flexible loan amounts" },
              { icon: FileText, title: "Minimum Documentation", desc: "Simple paperwork" },
              { icon: Calendar, title: "Same Week Disbursal Possible", desc: "Fast funding" },
              { icon: Building, title: "Multiple Bank & NBFC Tie-ups", desc: "Best offers for you" },
              { icon: Award, title: "Trusted MSME Loan Partner", desc: "In Varanasi since 2014" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <item.icon className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-16 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">MSME Loan Benefits</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Rocket, title: "Business Expansion", desc: "Start a new branch or setup" },
              { icon: Briefcase, title: "Working Capital", desc: "Keep daily operations smooth" },
              { icon: Factory, title: "Equipment Purchase", desc: "Boost productivity" },
              { icon: TrendingUp, title: "Cash Flow Management", desc: "Stabilize your business" },
              { icon: CreditCard, title: "Flexible EMI", desc: "Easy repayment options" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow">
                <item.icon className="w-6 h-6 text-green-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-800">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Who Can Apply */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Who Can Apply?</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Shop Owners & Retailers",
                "Traders & Wholesalers",
                "Manufacturers",
                "Startups & Entrepreneurs",
                "Service Providers",
                "Professionals (CA, Doctors, Consultants)"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-700 mt-6">
              → If you run a business, you are eligible!
            </p>
          </div>
        </div>

        {/* Loan Details */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Loan Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div>
              <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">💰 Loan Amount</p>
              <p className="text-xl font-bold text-gray-800">₹50,000 – ₹1 Crore+</p>
            </div>
            <div>
              <Percent className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">📉 Interest Rate</p>
              <p className="text-xl font-bold text-gray-800">Starting from 8%*</p>
            </div>
            <div>
              <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">📅 Tenure</p>
              <p className="text-xl font-bold text-gray-800">Flexible (12–60 months)</p>
            </div>
            <div>
              <Zap className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">⚡ Disbursal</p>
              <p className="text-xl font-bold text-gray-800">Fast</p>
            </div>
          </div>
        </div>

        {/* Documents Required */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Documents Required</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Aadhaar Card & PAN Card",
              "Address Proof",
              "Business Proof (GST / Registration)",
              "Bank Statement (Last 6 Months)",
              "ITR / Financial Documents"
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-green-600" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-600 mt-6">→ Simple documents, fast process!</p>
        </div>

        {/* Types of MSME Loans */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Types of MSME Loans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Briefcase, title: "Working Capital Loan" },
              { icon: TrendingUp, title: "Business Term Loan" },
              { icon: Factory, title: "Machinery Loan" },
              { icon: Rocket, title: "Startup Loan" },
              { icon: CreditCard, title: "Overdraft / Cash Credit" },
              { icon: Building, title: "Expansion Loan" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-all">
                <item.icon className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-16 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Simple 4‑Step Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Apply Online / Call", desc: "Fill the form or call us" },
              { step: "2", title: "Document Verification", desc: "We verify your documents" },
              { step: "3", title: "Loan Approval", desc: "Get approval quickly" },
              { step: "4", title: "Amount Disbursed", desc: "Funds in your account" }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-green-700 font-semibold mt-6">→ Loan ready in just 4 steps!</p>
        </div>

        {/* Why CashPro Best in Varanasi */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Why CashPro is the Best in Varanasi?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "✔ Trusted loan partner since 2014",
              "✔ High approval success rate",
              "✔ Transparent & hassle‑free process",
              "✔ Expert financial guidance",
              "✔ Strong banking network"
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <BadgeCheck className="w-5 h-5 text-green-600" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-green-700 font-semibold mt-6">→ CashPro = Your Business Growth Partner 💼</p>
        </div>

        {/* Strong Call to Action */}
        <div className="mb-16 bg-gradient-to-r from-green-700 to-green-800 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Grow Your Business?</h2>
          <p className="text-xl mb-6">→ Apply for an MSME Loan today and take your business to the next level!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+918887141052" className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center gap-2">
              <Phone size={18} /> Call / WhatsApp Now
            </a>
            <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300">
              Apply Online
            </button>
          </div>
          <p className="text-sm mt-6">📍 CashPro Financial Services, Varanasi</p>
          <p className="text-yellow-300 font-bold mt-2">🔥 Fast Loan. Easy Process. Maximum Growth.</p>
        </div>

        {/* SEO Content Block */}
        <div className="mb-16 text-center text-gray-500 text-sm border-t pt-8">
          <p>
            Looking for <strong className="text-green-600">MSME Loan in Varanasi</strong>? CashPro Financial Services offers the <strong>best business loan solutions</strong> with fast approval, low interest rates, and minimal documentation.
          </p>
          <p className="mt-2">
            Whether you need a <strong>small business loan, working capital loan, or unsecured MSME loan</strong>, we provide customized financial solutions to help your business grow.
          </p>
          <p className="mt-2">
            We are one of the <strong>best loan agencies in Varanasi</strong>, helping entrepreneurs, traders, and startups get quick funding with hassle‑free processing.
          </p>
        </div>

        {/* Lead Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Get a Free Consultation</h2>
          {submitted && (
            <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
              Thank you! Our team will contact you shortly.
            </div>
          )}
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
              <select
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select</option>
                <option>Shop / Retail</option>
                <option>Manufacturing</option>
                <option>Trading / Wholesale</option>
                <option>Service Provider</option>
                <option>Startup</option>
                <option>Other</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Amount Required (approx.)</label>
              <input
                type="text"
                name="loanAmount"
                value={formData.loanAmount}
                onChange={handleChange}
                placeholder="e.g., ₹5,00,000"
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="md:col-span-2 text-center">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Get Call Back
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-300 {
          animation-delay: 0.3s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
      `}</style>
    </div>
  );
};

export default MSMELoan;